import { useMemo } from 'react';
import PropTypes from 'prop-types';

import useDetailTable from '#/hooks/table/useDetailTable';
import { Button } from '#/modules/commonComponents/button';
import DetailTable from '#/modules/commonComponents/table/DetailTable';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import WorkOrderAmendmentStatusBadge from './WorkOrderAmendmentStatusBadge';
import { formatDateTimeOrDash } from '#/utils/date';

function buildAmendmentColumns(onPreview) {
  return [
    {
      id: 'preview',
      header: '預覽',
      size: 88,
      enableSorting: false,
      cell: ({ row }) => (
        <Button
          type="button"
          size="sm"
          onClick={() => onPreview?.(row.original)}
        >
          預覽
        </Button>
      ),
    },
    {
      id: 'amendmentNo',
      header: '單號',
      accessorFn: (row) => row?.amendmentNo ?? '—',
      size: 160,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={row.original.amendmentNo || '—'} />
      ),
    },
    {
      id: 'status',
      header: '狀態',
      accessorKey: 'status',
      size: 110,
      enableSorting: false,
      meta: { statusPresentation: 'badge' },
      cell: ({ row }) => (
        row.original.status
          ? <WorkOrderAmendmentStatusBadge status={row.original.status} size="sm" />
          : '—'
      ),
    },
    {
      id: 'issueReason',
      header: '事由',
      accessorFn: (row) => row?.issueReason ?? '',
      size: 200,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={row.original.issueReason || '—'} />
      ),
    },
    {
      id: 'createdAt',
      header: '建立時間',
      accessorFn: (row) => formatDateTimeOrDash(row?.record?.createdAt ?? row?.createdAt),
      size: 160,
      enableSorting: false,
    },
    {
      id: 'approvedAt',
      header: '核准時間',
      accessorFn: (row) => formatDateTimeOrDash(row?.history?.approvedAt),
      size: 160,
      enableSorting: false,
    },
  ];
}

const WorkOrderAmendmentsTable = ({ rows = [], onPreview }) => {
  const columns = useMemo(
    () => buildAmendmentColumns(onPreview),
    [onPreview],
  );

  const tableRows = useMemo(
    () => (rows ?? []).map((row, index) => ({
      ...row,
      _rowKey: row?.id != null && row.id !== ''
        ? `${row.id}-${index}`
        : `amendment-${index}`,
    })),
    [rows],
  );

  const detailTableState = useDetailTable(tableRows, columns, {
    enableExpanding: false,
    getRowId: (row) => String(row._rowKey),
  });

  if (tableRows.length === 0) {
    return <p className="px-4 py-5 text-sm text-muted-foreground">尚無異動單</p>;
  }

  return (
    <div className="detail-table-container">
      <DetailTable
        state={detailTableState}
        containerClassName="detail-table-scroll--section-embedded"
        tableClassName="detail-table--flat-lines"
      />
    </div>
  );
};

WorkOrderAmendmentsTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object),
  onPreview: PropTypes.func,
};

export default WorkOrderAmendmentsTable;
